import { Injectable, inject } from '@angular/core';
import {
  IIcLookupCostUnitTypeDataDto,
  IcLookupCostUnitTypeItemDto,
  IcLookupDto,
  IcLookupTypeEnumDto,
} from '@data-access/bulk-operations-api';

import { IcCostUnitValuesService } from './ic-cost-unit-values.service';
import { IcServiceBase } from './ic-service.base';

@Injectable({ providedIn: 'root' })
export class IcCostUnitTypesService extends IcServiceBase<
  IcLookupCostUnitTypeItemDto,
  IcCostUnitType
> {
  private readonly icCostUnitValuesService = inject(IcCostUnitValuesService);

  protected override lookupType = IcLookupTypeEnumDto.CostUnitType;

  protected override mapToLookupModel(value: IcLookupCostUnitTypeItemDto): IcCostUnitType {
    return {
      ...value.data,
      id: value.id,
      displayName: `${value.id} - ${value.data.name}`,
    };
  }

  protected override getValuesFromLookupDto(lookup: IcLookupDto): IcLookupCostUnitTypeItemDto[] {
    return lookup.costUnitTypes;
  }

  public getActiveCostUnitTypesForDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
  ): IcCostUnitType[] {
    const allCostUnitTypes = this.valuesSignal();
    if (!allCostUnitTypes?.length) return [];

    if (!dateFrom) {
      return allCostUnitTypes;
    }

    const checkDate = dateTo || dateFrom;

    const valuesArrayByType = this.icCostUnitValuesService.valuesArrayByTypeSignal();

    const filteredTypes = allCostUnitTypes.filter((type) => {
      const typeCoversFullRange =
        (!type.from || type.from <= dateFrom) && (!type.to || type.to >= checkDate);
      if (!typeCoversFullRange) return false;
      if (type.hasMoreValues) return true;

      const values = valuesArrayByType.get(type.id) || [];
      const activeValues = values.filter((value) => {
        const valueCoversFullRange =
          (!value.from || value.from <= dateFrom) && (!value.to || value.to >= checkDate);
        return !value.isGhost && valueCoversFullRange;
      });
      return activeValues.length > 0;
    });

    return filteredTypes.sort((a, b) => {
      const numA = parseInt(a.id, 10);
      const numB = parseInt(b.id, 10);
      return numA - numB;
    });
  }

  public getActiveCostUnitTypesForDateMap(
    dateFrom: Date | null,
    dateTo: Date | null = null,
  ): Map<string, IcCostUnitType> {
    const activeTypes = this.getActiveCostUnitTypesForDateRange(dateFrom, dateTo);
    return new Map(activeTypes.map((type) => [type.id, type]));
  }
}

export type IcCostUnitType = IIcLookupCostUnitTypeDataDto & { id: string; displayName: string };
